/** The spreadsheet round trip: export the inventory, edit it, see what would change, apply it (S06).
 *
 * **The dry run and the apply are two steps and stay two steps**, now that ADR-0039 is accepted
 * (2026-10-04). XLS-02's acceptance is *"preview lists creates, updates, unchanged, conflicts and
 * invalid rows; no mutation occurs"* — so the preview still writes nothing, and the apply is a
 * separate act by a narrower set of roles. Collapsing them into one upload would satisfy neither
 * requirement: the thing a person approves has to be the thing they have already looked at.
 *
 * Three things the screen has to say that a list of counts does not:
 *
 * - **`unchanged` is a number worth showing.** A reviewer shown only the edits has to take on trust
 *   that the other 990 rows were read at all.
 * - **A conflict is not an error.** Somebody corrected the reading after the file was exported, and
 *   the person looking at this did nothing wrong — the sentence says so.
 * - **Removing a row deletes nothing.** People filter and delete rows they are not interested in,
 *   and a screen that did not say this is a screen somebody will test by deleting a row.
 *
 * ## Who may change what (UX-04)
 *
 * *"Bulk correction previews affected records and fails unauthorized items explicitly."* The
 * preview half was built; the authorization half had **three defects, and the third is the one
 * worth remembering.**
 *
 * - **A Viewer and an Auditor were shown the file picker.** `POST /inventory/diff` is
 *   `require_roles(ADMIN, APPROVER, CONTRIBUTOR)`, so two of the five roles chose a file, waited,
 *   and collected a 403. A control that predictably fails is not a permission check, and the
 *   accept panel on the review screen already learned this (F12).
 * - **Every refusal rendered as the same sentence** — *"That file could not be read."* For a 403
 *   that is false, and it blames the reader's spreadsheet for a permission they do not hold. See
 *   `DiffRefused`.
 * - **The preview cannot report a per-row authorization, and now says so** rather than letting
 *   five dispositions imply that it can. That is stated on screen, not only here, because a
 *   reviewer reading *"12 changed"* will otherwise reasonably conclude all twelve are theirs to
 *   change. The honest version of a thing we cannot compute is a sentence, never a sixth
 *   disposition nothing sets.
 *
 * **What the server would have to add, if this is to become a real per-row answer.** `DiffRow`
 * carries no authorization at all, so a row the reader may not change is today indistinguishable
 * from one they may — except through the engagement clamp, which `roundtrip.diff` reports as
 * `invalid` with the sentence *"this row names a line that is not in this workspace and period"*.
 * That sentence is **false** in exactly the case UX-04 is about: the line is in the workspace, and
 * the principal's engagement does not reach it. Distinguishing the two needs a disposition, or a
 * flag on `DiffRow`, that only the server can set. It is not invented here.
 */

import { Check, Files } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";

import { tonnes } from "../lib/format";

import { HowDrawer, InfoPopover, PageHeaderCard, StatusChip } from "../components/ui";
import { Empty, Panel } from "../components/Panel";
import { StatusPill } from "../components/StatusPill";
import {
  DiffRefused,
  exportUrl,
  saveColumnMapping,
  useInventoryApply,
  useInventoryDiff,
} from "../api/queries";
import type {
  ColumnMap,
  ColumnsNeedMapping,
  DiffRow,
  InventoryApplied,
  InventoryDiff,
} from "../api/queries";
import { useMe } from "../api/auth";
import { bulkKey, clearDraft, fileDigest, readDraft, writeDraft } from "../lib/drafts";
import { useUploadPreCheck } from "../api/evidence";
import { isAmbiguous, isPeriodClosed } from "../api/refusal";
import { RequireSession } from "../shell/RequireSession";
import { NONE_OF_THESE, meterGroupKey } from "./meterGroup";
import { MeterSelect } from "./MeterPicker";
import { PostCloseRetry } from "./PeriodLock";
import { ScreenFrame } from "./ScreenFrame";
import styles from "./Screen.module.css";
import own from "./Bulk.module.css";
import type { Window } from "./useScreen";
import { RequirePeriod } from "./NoPeriod";
import { ColumnMapStep } from "./ColumnMapStep";
import type { Screen } from "./useScreen";

/** The roles `POST /inventory/diff` accepts, mirrored from `inventory.py`'s `MAY_EDIT`.
 *
 * **A mirror and not an authority.** The server refuses; this only decides what to render, which
 * is the one thing the server cannot do. Written as a list rather than `role !== "viewer"` so that
 * a sixth role added to the API defaults to *not shown the control* instead of silently inheriting
 * an edit surface it was never granted.
 */
const MAY_EDIT: readonly string[] = ["admin", "approver", "contributor"];

/** The roles `POST /inventory/apply` accepts, mirrored from `inventory.py`'s `MAY_APPLY`.
 *
 * **Deliberately not `MAY_EDIT`.** Two hundred corrections to an inventory that may already have
 * been filed is the most consequential bulk write in the product, so preparing it and accepting it
 * are different people's work — the same line ADR-0008 draws for a single document, drawn here for
 * a file of them. A Contributor sees the preview and is told who applies it.
 */
const MAY_APPLY: readonly string[] = ["admin", "approver"];

/** Each hazard a row can carry, named (EXP-10, and `RiskCode`'s own reason).
 *
 * **An acknowledgement of an unnamed risk is a checkbox.** `because` is one sentence covering
 * whatever the row happens to carry, and it is written to be reworded; the code is the fact, so the
 * reason a person types is attached to a hazard that was stated rather than to a general warning.
 *
 * Keyed by the server's code, with the code itself as the fallback label — never a guess. A hazard
 * this screen has not been taught is still shown, because dropping it would ask somebody to
 * acknowledge a row while hiding one of the reasons it needs acknowledging.
 */
const RISK: Record<string, string> = {
  unit_changed: "the unit changes",
  category_changed: "the category changes",
  fuel_changed: "the fuel changes",
  period_moved: "the period moves",
  closed_period: "it writes into a period this company has closed",
};

/** Why there is nothing to apply — five codes, five different sentences.
 *
 * **Absence must not read as success.** The server sends a code rather than a sentence for DOC-01's
 * reason, and the two cases that matter most are the ones a quiet "nothing to do" would get wrong:
 * a file whose every row was refused, and a file naming rows nobody selected. Both say so here.
 */
const NOTHING: Record<string, string> = {
  no_rows: "This file has no rows in it.",
  nothing_selected:
    "No row is selected. Nothing is wrong with the file — tick the rows you want applied, or clear the selection to apply all of them.",
  all_already_applied: "",
  all_unchanged: "",
  all_blocked:
    "Every row of this file was refused, which is not the same as there being nothing to do. Each refusal is named above — a conflict needs a fresh export, and a row that cannot be read needs fixing in the file.",
  nothing_applicable:
    "No row of this file would write anything. The reasons are per row above, and they are not all the same kind of reason.",
};

/** What each disposition means, in the reader's terms rather than the schema's.
 *
 * `delete` is absent because the product has no such disposition, and the empty state below says
 * so in words — a legend that quietly omitted it would leave somebody wondering.
 */
const DISPOSITION: Record<string, { label: string; blurb: string }> = {
  create: { label: "New", blurb: "no line id, so it would be added as a new reading" },
  update: { label: "Changed", blurb: "fields differ from the reading in force" },
  unchanged: { label: "Unchanged", blurb: "identical to the reading in force" },
  conflict: {
    label: "Conflict",
    blurb: "corrected after the file was exported — your edit would discard that correction",
  },
  invalid: { label: "Cannot read", blurb: "the row cannot be read, or names a line we do not have" },
  // Not a disposition the server sets — the screen derives it from `outside_window` on an
  // `invalid` row. A sixth disposition would have been the thing this file argues against
  // elsewhere; a flag on a row the server can actually set is what UX-04 asked for.
  outside: {
    label: "Another period",
    blurb: "a reading we hold, outside the window you are comparing against",
  },
};

/** A row an earlier apply of this same file already landed (G-Wapprover-17): the server calls it a
 * conflict, because the revision it names was superseded, and **by that apply** - so the label says
 * what happened and not that a stranger got there first. */
const ALREADY_DONE = {
  label: "Already done",
  blurb: "an earlier apply of this file already made this change",
};

function cls(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

/** A role in the reader's words. "An Auditor", not `auditor`.
 *
 * Named rather than echoed because the sentence it goes into is about *them*, and a screen that
 * tells somebody their role is `viewer` is telling them a database value. The fallback is
 * deliberately vague: an unrecognised role is one this screen does not know how to describe, and
 * inventing a name for it would be worse than a general sentence.
 */
function roleName(role: string | undefined): string {
  if (role === "auditor") return "An Auditor";
  if (role === "viewer") return "A Viewer";
  return "Your role";
}

/** How many new rows the preview asks a meter question about - the count the dry run states. */
function meterRows(diff: InventoryDiff): number {
  return (diff.meter_groups ?? []).reduce((total, group) => total + group.rows, 0);
}

type StepState = "done" | "now" | "next" | "skipped";
interface StepSpec {
  key: string;
  label: string;
  state: StepState;
}

const STEP_WORD: Record<StepState, string> = {
  done: "Done",
  now: "Now",
  next: "Next",
  skipped: "Not needed",
};

/** The four steps, in order. A state is a word and a shape (a tick when done, the number
 * otherwise), never colour alone; the current one carries `aria-current="step"`. The stepper only
 * *says* where the person is: it holds no control, and **nothing here gates the apply** - the
 * gates are `ApplyStep`'s, which is where the server's demands are answered. */
function Stepper({ steps }: { steps: readonly StepSpec[] }) {
  return (
    <ol className={own.stepper} aria-label="Steps" data-testid="bulk-steps">
      {steps.map((step, index) => (
        <li
          key={step.key}
          className={own.step}
          data-state={step.state}
          aria-current={step.state === "now" ? "step" : undefined}
        >
          <span className={own.stepMark} aria-hidden="true">
            {step.state === "done" ? <Check size={13} weight="bold" /> : index + 1}
          </span>
          <span className={own.stepLabel}>{step.label}</span>
          <span className={own.stepState}>{STEP_WORD[step.state]}</span>
        </li>
      ))}
    </ol>
  );
}

function BulkScreenBody({ screen, period }: { screen: Screen; period: Window }) {
  const diff = useInventoryDiff(period);
  const preCheck = useUploadPreCheck();
  const input = useRef<HTMLInputElement>(null);
  const [result, setResult] = useState<InventoryDiff | null>(null);
  const [filename, setFilename] = useState<string>("");
  //: **The file, not another one.** Applying re-sends the very `File` the preview ran against, so
  //: what a reviewer approved is what runs. See `useInventoryApply`.
  const [chosen, setChosen] = useState<File | null>(null);
  //: The refusal, as the refusal rather than as a sentence. `DiffRefused` carries the status and
  //: the API's own code, so the screen can tell "you may not do this" from "we could not read
  //: your file" — which it could not when every failure arrived as `new Error(message)`.
  const [refused, setRefused] = useState<DiffRefused | Error | null>(null);
  const [showing, setShowing] = useState<string>("changes");
  //: **The file's columns are not ours, and a person has to say which is which** (Gate 38). Held
  //: apart from `refused` because it is not a refusal of the file: the server read it, found
  //: nothing wrong with it, and asked. Cleared with the file and with a result.
  const [mapping, setMapping] = useState<ColumnsNeedMapping | null>(null);
  //: The sentence if the server refused a mapping that was sent, shown beside the step it came from.
  const [mappingRefusal, setMappingRefusal] = useState<string | null>(null);
  //: **The mapping the comparison on screen was read with**, sent again by the apply. The apply
  //: re-reads the file, and a file read one way for the look and another for the write is the
  //: thing a reviewer approved not being the thing that ran.
  const [columnMap, setColumnMap] = useState<ColumnMap | null>(null);
  //: **ADR-0104: what happened to "remember this mapping"**, said beside the comparison it followed.
  //: The save runs only after the comparison accepted the mapping, and a failure to save is a
  //: sentence here rather than a reason to withhold a result that already exists.
  const [rememberNote, setRememberNote] = useState<string | null>(null);
  //: **A workbook refused in the browser, before anything was sent** (ADR-0058). Its own state and
  //: not `refused`: that holds what the *server* answered, and here no request was made — which is
  //: also why nothing is chosen, and so nothing can be applied, afterwards.
  const [oversize, setOversize] = useState<string | null>(null);
  //: **The rows deliberately left out, not the rows chosen** (EXP-10). The default is the whole
  //: file, so the state that needs holding is the set somebody has said no to — which makes
  //: "nothing ticked" mean *apply all of it* rather than *apply none of it*. Stored the other way
  //: round, an empty set would be ambiguous between the two, and the server refuses that ambiguity
  //: by taking `select_rows` as its own flag rather than inferring it from an empty list.
  const [left, setLeft] = useState<ReadonlySet<number>>(new Set());
  //: Why, per high-risk row, keyed by the line in **this** file. A reason typed against row 7 of
  //: one file must never travel to row 7 of the next, so both this and `left` are cleared whenever
  //: a file is chosen.
  const [reasons, setReasons] = useState<Readonly<Record<number, string>>>({});
  //: **The meter a person chose for each group of new rows, by `meterGroupKey`** (ADR-0103).
  //: Empty is *not answered yet* and is the only default: nothing is pre-selected, suggested or
  //: filled from the file's own `meter` column (SEC-05). `NONE_OF_THESE` is an answer. Cleared with
  //: the file, for the reason `reasons` is: an answer is about one file's groups.
  const [meters, setMeters] = useState<Readonly<Record<string, string>>>({});
  //: Whether the server has answered an apply for the comparison on screen. Only the stepper reads
  //: it; the apply step keeps its own result.
  const [applied, setApplied] = useState(false);
  const { data: me } = useMe();
  /* **The decisions on a long comparison survive a refresh** (EXP-35, ADR-0112). A browser cannot
   * keep a file, so the file is the one thing a person must choose again; what is kept is the
   * choices made about *that file's* rows - the rows left out and the meter answers - under the
   * person's own key and the file's SHA-256, so a different file never receives them. They are put
   * back only once the comparison has arrived and only where they still apply (a row the file no
   * longer offers, or a meter the group no longer lists, is dropped). **Nothing is applied by
   * restoring**, and the reasons typed for high-risk rows are never kept: see `lib/drafts.ts`. */
  const [fileHash, setFileHash] = useState<string | null>(null);
  const draftKey =
    me && fileHash ? bulkKey(me, fileHash, period.period_start, period.period_end) : null;
  const [draftReady, setDraftReady] = useState<string | null>(null);
  const [restored, setRestored] = useState<{ rows: number; meters: number } | null>(null);
  //: Restored during render once the comparison has arrived (see `Review.tsx` for why not an effect).
  if (draftKey !== null && result !== null && draftReady !== draftKey) {
    setDraftReady(draftKey);
    const saved = readDraft(draftKey, "bulk");
    const selectable = new Set(result.selectable_lines ?? []);
    const rows = (saved.left ?? []).filter((line) => selectable.has(line));
    const answers: Record<string, string> = {};
    for (const group of result.meter_groups ?? []) {
      const answer = saved.meters?.[meterGroupKey(group)];
      if (answer === NONE_OF_THESE || group.meters.some((meter) => meter.meter_id === answer)) {
        answers[meterGroupKey(group)] = answer as string;
      }
    }
    if (rows.length > 0) setLeft(new Set(rows));
    if (Object.keys(answers).length > 0) setMeters(answers);
    setRestored(
      rows.length + Object.keys(answers).length > 0
        ? { rows: rows.length, meters: Object.keys(answers).length }
        : null,
    );
  }
  useEffect(() => {
    if (draftKey === null || draftReady !== draftKey) return;
    writeDraft(draftKey, "bulk", { left: [...left], meters });
  }, [draftKey, draftReady, left, meters]);
  //: `undefined` while `/auth/me` is in flight, and that is **not** the same as "may not edit": a
  //: screen that rendered the refusal panel for a second on every load would teach a Contributor
  //: to ignore it. So the control is withheld until the answer is known, and nothing is asserted.
  const mayEdit = me ? MAY_EDIT.includes(me.role) : undefined;
  //: Narrower than `mayEdit` on purpose, and mirrored from `inventory.py`'s `MAY_APPLY`: a
  //: Contributor may run the comparison and may not apply it (ADR-0039). The same separation the
  //: review screen makes between reading an extraction and accepting one (F12).
  const mayApply = me ? MAY_APPLY.includes(me.role) : undefined;

  /** Compare `file`, read by its own column names or by the mapping a person confirmed. */
  async function compare(
    file: File,
    map: ColumnMap | null,
    remember: { name: string } | null = null,
  ) {
    setRefused(null);
    setMappingRefusal(null);
    setRememberNote(null);
    try {
      setResult(await diff.mutateAsync({ file, columnMap: map }));
      setColumnMap(map);
      setMapping(null);
      if (map !== null && remember !== null) {
        //: After the comparison, never before: a mapping the parser refused must not be kept.
        const failed = await saveColumnMapping(remember.name, map);
        setRememberNote(
          failed === null
            ? `Mapping “${remember.name}” remembered. The next file with exactly these columns will be offered it, not given it.`
            : `This mapping was not remembered: ${failed}`,
        );
      }
    } catch (error) {
      if (error instanceof DiffRefused && error.needsMapping && error.columns) {
        //: Not a refusal to render as one: the file is fine and the screen has a way forward.
        setMapping(error.columns);
      } else if (map !== null) {
        //: A mapping the server would not use stays open beside its reason, so it can be changed.
        setMappingRefusal(error instanceof Error ? error.message : "That mapping could not be used.");
      } else {
        setRefused(error instanceof Error ? error : new Error("That file could not be read."));
      }
    }
  }

  async function onFile(file: File | undefined) {
    if (!file) return;
    setRefused(null);
    setResult(null);
    setOversize(null);
    setMapping(null);
    setMappingRefusal(null);
    setColumnMap(null);
    setRememberNote(null);
    //: **Before the request, because over 10 MiB the browser is given no response to render.** The
    //: platform refuses a body over its cap before the application runs, and from 10 MiB without
    //: the CORS headers, so `fetch` rejects with no status and no message — which `refused` below
    //: would render as a server nobody could reach. Both this route and the apply sit under the
    //: same cap as an evidence upload, and `useInventoryApply` re-sends the file chosen here, so
    //: checking once, at the choice, covers both.
    const tooLarge = await preCheck(file);
    if (tooLarge) {
      //: The server's remedy is about scans and statements, so it is not borrowed: this file is a
      //: workbook, and the export it came from is per period.
      setOversize(
        `${tooLarge.lead} Nothing was compared and nothing was written. Download a workbook for ` +
          "a shorter period and send that instead.",
      );
      setFilename("");
      setChosen(null);
      if (input.current) input.current.value = "";
      return;
    }
    setFilename(file.name);
    setChosen(file);
    //: **Both cleared, together with the result.** A reason is about one row of one file; carried
    //: over to the next file it would attach somebody's sentence about a unit change to whatever
    //: row happens to land on the same line number.
    setLeft(new Set());
    setReasons({});
    setMeters({});
    setApplied(false);
    setRestored(null);
    setDraftReady(null);
    setFileHash(await fileDigest(file));
    await compare(file, null);
  }

  const rows = result?.rows ?? [];
  const shown =
    showing === "all"
      ? rows
      : rows.filter((row) => row.disposition !== "unchanged");

  //: **The two reasons a row is high-risk, counted from the rows themselves** (G-Wapprover-14).
  //: `risks` is the server's list of codes, so the banner states what each row carries and nothing
  //: it does not: a row whose only hazard is a closed period is not "a changed unit".
  const fieldRiskRows = rows.filter((row) =>
    (row.risks ?? []).some((code) => code !== "closed_period"),
  );
  const closedRiskRows = rows.filter((row) => (row.risks ?? []).includes("closed_period"));
  const closedNames = [...new Set(closedRiskRows.flatMap((row) => row.closed_periods ?? []))];

  //: **Where the person is, derived from what is on screen and from nothing else.** Mapping is
  //: *Not needed* when the file's own column names were read (`columnMap` is null once a result
  //: exists) and *Done* when a person confirmed a mapping. Review and Apply are one state apart on
  //: purpose: the apply panel is on screen from the first comparison, and a stepper that marked
  //: step 3 done the moment a result arrived would be claiming the reviewer had looked.
  const steps: StepSpec[] = [
    { key: "upload", label: "Upload", state: result || mapping ? "done" : "now" },
    {
      key: "map",
      label: "Map columns",
      state: mapping && !result ? "now" : result ? (columnMap ? "done" : "skipped") : "next",
    },
    { key: "review", label: "Review changes", state: !result ? "next" : applied ? "done" : "now" },
    { key: "apply", label: "Apply", state: applied ? "done" : "next" },
  ];

  return (
    <ScreenFrame
      screen={screen}
      title="Bulk edit"
      crumb="export, edit, and see what would change"
      bodyScrolls
    >
      <PageHeaderCard icon={Files} title="Bulk edit" label="Documents" />
      <Stepper steps={steps} />

      <Panel title="1 · Upload" column="1 / 13">
        <div className={cls(own.upload)}>
          <div className={cls(own.uploadBlock)} data-testid="bulk-export">
            <h3 className={cls(own.blockTitle)}>
              Take the inventory out
              <InfoPopover infoKey="documents.bulk-export" />
            </h3>
            <p className={cls(own.actions)}>
              <a className={styles.primary} href={exportUrl(period)} download>
                Download the workbook
              </a>
            </p>
          </div>

          <div className={cls(own.uploadBlock)}>
            <h3 className={cls(own.blockTitle)}>
              Send it back
              <InfoPopover infoKey="documents.bulk-send" />
            </h3>

        {/* **The role decides whether there is a control, not whether it works** (UX-04).
            A Viewer and an Auditor were shown this picker and got a 403 after choosing a file and
            waiting — and the 403 then rendered as "that file could not be read", so they went and
            re-saved their spreadsheet. Told instead, with who can, which is the pattern the review
            panel already uses for accepting a document (F12). */}
        {mayEdit === false ? (
          <p className={cls(own.unauthorized)} data-testid="not-permitted">
            <StatusChip tone="warn" size="sm">
              {roleName(me?.role)} cannot change readings
            </StatusChip>{" "}
            <InfoPopover infoKey="legacy.bulk-role" />
          </p>
        ) : (
          <p className={cls(own.actions)}>
            <input
              ref={input}
              type="file"
              aria-label="The edited workbook"
              accept=".xlsx,.csv,text/csv"
              // `undefined` while the session is still loading: a picker that accepts a file
              // before anyone knows whether this role may use it is a picker that 403s.
              disabled={mayEdit === undefined}
              onChange={(event) => void onFile(event.target.files?.[0])}
            />
            {diff.isPending ? <span className={styles.note}>Comparing…</span> : null}
          </p>
        )}

        {oversize ? (
          <p className={styles.error} role="alert" data-testid="file-too-large">
            {oversize}
          </p>
        ) : null}

        {/* **Three refusals, three sentences** (UX-04, EXP-29's "failures are not empty success").
            They were one. A permission, an engagement that does not reach this period, and a file
            this product genuinely cannot parse are three different things to go and do, and the
            paragraph a reader got named the third whichever it was. */}
        {refused ? (
          <p className={styles.error} role="alert" data-testid="diff-refused">
            {isAmbiguous(refused) ? (
              /* **A comparison writes nothing**, so an unknown outcome here is safe and the
                 remedy is simply to send it again — which is why this says so rather than
                 borrowing the apply panel's careful sentence. What it must not say is "that file
                 could not be read", which blames the spreadsheet for a server nobody reached. */
              <>
                <strong>EmitIQ could not be reached.</strong> Nothing was compared and nothing
                was written — a comparison never writes. Send {filename || "the file"} again.{" "}
                {/* **The status, still named.** It is the only thing about this that is true, and
                    the sentence it replaced said so for the same reason — what changed is that it
                    no longer blames the reader's spreadsheet for a gateway. */}
                {refused.message}
              </>
            ) : refused instanceof DiffRefused && refused.isUnauthorized ? (
              <>
                <strong>Refused: your role cannot change readings.</strong> Nothing in{" "}
                {filename || "that file"} was applied, and <strong>no row of it was compared</strong>{" "}
                — the request was refused before the file was read, so this is not a result about
                your spreadsheet. An Admin, an Approver or a Contributor can run it.
              </>
            ) : refused instanceof DiffRefused && refused.isOutsidePeriod ? (
              <>
                <strong>Refused: this period is outside what you are engaged for.</strong> The
                readings in {filename || "that file"} belong to a window you may not see, so there
                was nothing to compare them against — which is a different thing from the inventory
                being empty. Choose a period you are engaged for in the bar above.
              </>
            ) : (
              <>
                <strong>That file could not be read.</strong> {refused.message}
              </>
            )}
          </p>
        ) : null}

          </div>
        </div>
      </Panel>

      {mapping && !result && chosen ? (
        <Panel
          title="2 · Map columns"
          sub="this file's columns are not ours: say which is which"
          column="1 / 13"
        >
          <ColumnMapStep
            key={filename}
            needs={mapping}
            filename={filename}
            busy={diff.isPending}
            refusal={mappingRefusal}
            onConfirm={(map, remember) => void compare(chosen, map, remember)}
            onCancel={() => {
              setMapping(null);
              setMappingRefusal(null);
              setChosen(null);
              setFilename("");
              if (input.current) input.current.value = "";
            }}
          />
        </Panel>
      ) : null}

      {result ? (
        <>
          <Panel
            title="3 · Review what would change"
            sub={filename || undefined}
            column="1 / 13"
          >
            {/* **Cancel, on the screen whose "form" is a chosen file.** A reviewer who opens the
                wrong export has nowhere to go but a page reload — and a reload keeps the file
                input's value, so the comparison comes straight back. This drops the file, the
                comparison and the refusal together. */}
            <p className={cls(own.actions)}>
              <button
                type="button"
                className={styles.fixLink}
                data-testid="discard-diff"
                onClick={() => {
                  setResult(null);
                  setChosen(null);
                  setFilename("");
                  setRefused(null);
                  setOversize(null);
                  setMapping(null);
                  setMappingRefusal(null);
                  setColumnMap(null);
                  setRememberNote(null);
                  setLeft(new Set());
                  setReasons({});
                  setMeters({});
                  setApplied(false);
                  setRestored(null);
                  setDraftReady(null);
                  setFileHash(null);
                  if (input.current) input.current.value = "";
                }}
              >
                Discard this comparison and choose another file
              </button>
            </p>
            {rememberNote ? (
              <p className={styles.note} role="status" data-testid="mapping-remembered">
                {rememberNote}
              </p>
            ) : null}
            <div className={cls(own.counts)} data-testid="diff-counts">
              <Count n={result.creates} label="New" tone="ok" />
              <Count n={result.updates} label="Changed" tone="ok" />
              <Count n={result.unchanged} label="Unchanged" />
              {/* **A conflict somebody else caused, and what this file already did, counted apart**
                  (G-Wapprover-17). A re-sent file's rows are conflicts to the server (the revision
                  they name was superseded by *that apply*), and a tile reading "10 Conflicts" beside
                  a promise that sending the same file again is safe accused a stranger. */}
              <Count
                n={Math.max(result.conflicts - result.already_applied, 0)}
                label="Conflicts"
                tone="warn"
              />
              {result.already_applied > 0 ? (
                <Count n={result.already_applied} label="Already done" />
              ) : null}
              <Count n={result.invalid - result.outside_window} label="Cannot read" tone="crit" />
              {/* **Counted apart from "Cannot read"** (UX-04). A file whose rows are all for the
                  wrong year is a different problem from a file with bad data in it, and the remedy
                  is different: export the right period, rather than fix the rows. */}
              <Count n={result.outside_window} label="Another period" tone="warn" />
            </div>

            {/* XLS-05, said before somebody tests it by deleting a row. */}
            {result.absent_line_ids.length > 0 ? (
              <p className={cls(own.absent)}>
                {result.absent_line_ids.length === 1
                  ? "1 reading is in the inventory and not in your file."
                  : `${result.absent_line_ids.length} readings are in the inventory and not in your file.`}{" "}
                <strong>
                  Nothing happens to {result.absent_line_ids.length === 1 ? "it" : "them"}.
                </strong>{" "}
                Removing a row from a spreadsheet does not delete anything here.
              </p>
            ) : null}

            {/* XLS-03: a unit or a period change is never routine metadata, and a reviewer
                approving "12 changed" must not have to find them. **Two reasons, two sentences,
                each counted from the rows that carry it** (G-Wapprover-14): the banner used to say
                a unit, category, fuel or period changed over rows whose only change was a quantity
                inside a closed year, so the reason a person read was not the reason on the row. */}
            {fieldRiskRows.length > 0 ? (
              <p className={cls(own.sensitive)} data-testid="sensitive-field-changes">
                {fieldRiskRows.length === 1
                  ? "One row changes a unit, a category, a fuel or a period."
                  : `${fieldRiskRows.length} rows change a unit, a category, a fuel or a period.`}{" "}
                That is not metadata: 100 therms is not 100 kWh, and a category moves a figure
                between scopes.
              </p>
            ) : null}
            {closedRiskRows.length > 0 ? (
              <p className={cls(own.sensitive)} data-testid="sensitive-closed-period">
                {closedRiskRows.length === 1
                  ? "One row writes into a closed period"
                  : `${closedRiskRows.length} rows write into a closed period`}{" "}
                ({closedNames.join("; ")}). Only an Admin can apply{" "}
                {closedRiskRows.length === 1 ? "it" : "them"}, with a reason.
              </p>
            ) : null}

            {/* **The dry run tells the person a meter question is coming** (ADR-0103), before they
                reach the apply, and says what it is about. The count is of rows and the wording is
                fixed: nothing in it depends on what the file's `meter` column says. */}
            {(result.meter_groups ?? []).length > 0 ? (
              <p className={cls(own.pending)} data-testid="meter-groups-note">
                <strong>
                  {meterRows(result) === 1
                    ? "One new row needs a meter chosen before it can be applied."
                    : `${meterRows(result)} new rows need a meter chosen before they can be applied.`}
                </strong>{" "}
                {meterRows(result) === 1 ? "It is" : "They are"} at a site that expects readings
                from particular meters, and a reading recorded without one does not count towards
                that meter&rsquo;s stream. Whoever applies this file says which meter each group came
                off, or that it came off none of them. The <code>meter</code> column of your file is
                not read for this &mdash; a meter is chosen by a person, never taken from a cell.
              </p>
            ) : null}

            {result.notes.map((note) => (
              <p key={note} className={styles.note}>
                {note}
              </p>
            ))}

            <div className={cls(own.filters)}>
              <span className={cls(own.filename)}>{filename}</span>
              <label className={cls(own.toggle)}>
                <input
                  type="checkbox"
                  checked={showing === "all"}
                  onChange={(event) => setShowing(event.target.checked ? "all" : "changes")}
                />
                <span>Show unchanged rows too</span>
              </label>
            </div>

            {shown.length === 0 ? (
              <Empty>
                Nothing in this file would change anything. Every row matches the reading already
                held.
              </Empty>
            ) : (
              <ul className={cls(own.rows)}>
                {shown.map((row) => (
                  <Row
                    key={`${row.line}-${row.disposition}`}
                    row={row}
                    //: The controls are an Approver's, because applying is. A Contributor sees the
                    //: comparison and is told who applies it, so offering them a reason box would
                    //: be collecting an answer nobody will send (UX-04).
                    editable={mayApply === true}
                    //: **Only an Admin writes into a closed period** (ADR-0094 Q2). Told on the row,
                    //: with no reason box for a person who cannot apply it (G-Wapprover-15).
                    isAdmin={me?.role === "admin"}
                    selected={!left.has(row.line)}
                    onSelect={(wanted) =>
                      setLeft((was) => {
                        const next = new Set(was);
                        if (wanted) next.delete(row.line);
                        else next.add(row.line);
                        return next;
                      })
                    }
                    reason={reasons[row.line] ?? ""}
                    onReason={(words) => setReasons((was) => ({ ...was, [row.line]: words }))}
                    onReceipt={screen.openReceipt}
                  />
                ))}
              </ul>
            )}

            {/* **What the preview does not check, said where the counts are** (UX-04).
                Five dispositions and a count against each of them read as a complete account of
                every row, so a reviewer looking at "12 changed" concludes all twelve are theirs to
                change. They may not be — and this preview cannot tell them which, because the
                server classifies a row against the inventory and not against the reader. Saying so
                is the honest version; a sixth disposition nothing can set would look like the
                check existing. */}
            <p className={cls(own.unchecked)} data-testid="row-authorization">
              <strong>What was checked, row by row, and what was not.</strong> Your role may run
              this comparison. A row naming a reading we hold outside this window — because it
              belongs to another period, or to a part of this one you are not engaged for — is
              marked <strong>Another period</strong> and is <em>not</em> a fault in your file;
              export the period it belongs to and edit that. The one permission it does report per
              row is a closed period: a row that writes into one is marked, because only an Admin
              can apply it. What is still <em>not</em> checked is whether you may change each
              specific reading: a per-reading permission is not something EmitIQ reports.
            </p>

            {restored ? (
              <p className={styles.note} role="status" data-testid="bulk-draft-restored">
                <strong>Your earlier choices on this file were put back</strong>
                {" "}({restored.rows} row{restored.rows === 1 ? "" : "s"} left out, {restored.meters}{" "}
                meter answer{restored.meters === 1 ? "" : "s"}). Nothing has been applied. The reasons
                you wrote for high-risk rows are never kept, so those are asked again.{" "}
                <button
                  type="button"
                  className={styles.fixLink}
                  data-testid="bulk-draft-discard"
                  onClick={() => {
                    clearDraft(draftKey);
                    setLeft(new Set());
                    setMeters({});
                    setRestored(null);
                  }}
                >
                  Start again
                </button>
              </p>
            ) : null}

          </Panel>

          <Panel
            title="4 · Apply"
            sub="the only step that writes"
            column="1 / 13"
          >
            <ApplyStep
              diff={result}
              file={chosen}
              filename={filename}
              mayApply={mayApply}
              role={me?.role}
              period={period}
              left={left}
              reasons={reasons}
              meters={meters}
              onMeter={(key, meterId) => setMeters((was) => ({ ...was, [key]: meterId }))}
              columnMap={columnMap}
              onApplied={() => {
                clearDraft(draftKey);
                setApplied(true);
              }}
            />
          </Panel>
        </>
      ) : null}

      <HowDrawer infoKey="documents.bulk-edit" label="Basis and limits" />
    </ScreenFrame>
  );
}

/** The third step: apply what the preview showed (ADR-0039, XLS-04).
 *
 * **It is a confirmation and not a button**, which is the one design decision here worth arguing
 * for. Every other write in this product is one record at a time; this one moves as many figures as
 * the file has rows, in an inventory that may already have been filed. So the count is said twice —
 * once on the arming control and once in the confirmation — and the sentence names what will not
 * happen as well as what will.
 *
 * Four things it must say, because the counts alone imply the opposite of each:
 *
 * - **Conflicts are refused one at a time, and the rest still apply.** XLS-04: a file that someone
 *   else has partly overtaken is not a file to send back whole. A reader who is not told this will
 *   reasonably expect all-or-nothing and conclude the apply half-failed.
 * - **Sending the same file again changes nothing a second time.** That is the whole of ADR-0039,
 *   and the person most in need of hearing it is the one whose request just timed out. It is said
 *   *before* the apply, not only afterwards, because that is when they are deciding.
 * - **`already_applied` is its own number.** Collapsed into `unchanged` it would tell a retrying
 *   reviewer their file was a no-op, which is exactly the reassurance they should not be given.
 * - **Nothing is deleted, ever.** An apply writes superseding revisions (ADR-0030); the figure it
 *   replaces keeps its receipt. A bulk write is the moment somebody fears losing history.
 *
 * It does **not** re-pick the file: `file` is the `File` the preview ran against. A second picker
 * would let the bytes change between looking and applying.
 */
function ApplyStep({
  diff,
  file,
  filename,
  mayApply,
  role,
  period,
  left,
  reasons,
  meters,
  onMeter,
  columnMap,
  onApplied,
}: {
  diff: InventoryDiff;
  file: File | null;
  filename: string;
  mayApply: boolean | undefined;
  role: string | undefined;
  period: Window;
  left: ReadonlySet<number>;
  reasons: Readonly<Record<number, string>>;
  meters: Readonly<Record<string, string>>;
  onMeter: (groupKey: string, meterId: string) => void;
  columnMap: ColumnMap | null;
  onApplied: () => void;
}) {
  const apply = useInventoryApply(period);
  const [arming, setArming] = useState(false);
  const [done, setDone] = useState<InventoryApplied | null>(null);
  const [refused, setRefused] = useState<DiffRefused | Error | null>(null);

  //: **The rows this request would write**, which is the selection intersected with what the
  //: server says is applicable at all. Computed here rather than taken from `left` alone, because
  //: un-ticking an unchanged row changes nothing and must not turn a whole-file apply into a
  //: partial one — `select_rows` is a different request and the server treats it as such.
  const selectable = diff.selectable_lines ?? [];
  //: **Rows only an Admin can apply, for somebody who is not one** (ADR-0094 Q2, G-Wapprover-15).
  //: Told on the diff, and left out of what this person applies: the server would otherwise refuse
  //: the whole file after ten reasons and a confirm. They count as *left out*, so the request is a
  //: selection of the rest. Absent from a response that did not carry the field, which is the old
  //: behaviour (the server still refuses) and never a row applied that should not be.
  const held: readonly number[] = role === "admin" ? [] : (diff.admin_only_lines ?? []);
  const gone = new Set<number>([...left, ...held]);
  const chosenLines = selectable.filter((line) => !gone.has(line));
  const partial = chosenLines.length < selectable.length;
  //: Whether *this person* left rows out, for the words: rows an Admin applies are not a choice, so
  //: "apply 2 of 2" is never said when the only rows missing are those.
  const choseFewer = chosenLines.length < selectable.length - held.length;

  //: **Every high-risk row being written, and nothing else.** Mirrored from `applied.unacknowledged`
  //: — a row nobody selected is one they said no to, and the server stops asking. Asking anyway
  //: would hold the apply on a reason for a row that is not being applied.
  //:
  //: **Keyed on `left`, deliberately not on `selectable_lines`.** Intersecting with the server's
  //: selectable set reads as the more careful version and is the dangerous one: a response that
  //: did not carry that field would make this empty, the refusal would never fire, and the apply
  //: would 422 again — the exact defect this repair exists for, restored by a line that looks like
  //: a safety check. The only thing that excuses a row from needing a reason is somebody saying
  //: no to it, and that fact is held here rather than fetched.
  const owed = (diff.acknowledgement_lines ?? []).filter(
    (line) => !gone.has(line) && !(reasons[line] ?? "").trim(),
  );
  //: The same rows before the reason is taken off: every high-risk row being written. Said as a
  //: count beside the button; `owed` alone is what holds it.
  const requiring = (diff.acknowledgement_lines ?? []).filter((line) => !gone.has(line));

  //: **The meter questions the rows being written actually ask** (ADR-0103). A group is *in play*
  //: when at least one of its rows is selected, and only an in-play group holds the apply - the
  //: server asks the same of the same rows (`applied.apply` offers only what it would write). Keyed
  //: on `left` and on each row's own `meter_group`, deliberately not on a count: a group whose every
  //: row was un-ticked is a question nobody is asking, and holding the button on it would be a
  //: control that cannot be satisfied.
  const meterGroups = diff.meter_groups ?? [];
  const inPlay = new Set(
    (diff.rows ?? [])
      .filter((row) => row.needs_meter_choice && row.meter_group && !gone.has(row.line))
      .map((row) => row.meter_group as string),
  );
  const asked = meterGroups.filter((group) => inPlay.has(meterGroupKey(group)));
  const unanswered = asked.filter((group) => !meters[meterGroupKey(group)]);
  //: Every answered group, in play or not: the server validates an answer against the file's new
  //: rows, not only the selected ones, so a ticked-off group's answer is harmless and dropping it
  //: here would only make a retry differ from the first send.
  const meterChoices = meterGroups.flatMap((group) => {
    const chosen = meters[meterGroupKey(group)];
    if (!chosen) return [];
    return [
      {
        facility_id: group.facility_id,
        category: group.category,
        fuel_or_carrier: group.fuel_or_carrier,
        meter_id: chosen === NONE_OF_THESE ? null : chosen,
      },
    ];
  });

  const changes = diff.creates + diff.updates;
  //: What this person would apply: the changes less the rows an Admin applies.
  const canApply = changes - held.length;
  //: **Rows this file has already applied** (ADR-0039). Counted apart from both the changes and
  //: the conflicts, because it is neither: the reading was corrected, by this file, and the
  //: revision the row names is superseded as a result. Reported as a conflict it accused a
  //: stranger; reported as nothing it told somebody retrying a timed-out request that their file
  //: was empty.
  const done_before = diff.already_applied;
  //: A conflict somebody *else* caused — the only kind worth re-exporting for.
  const overtaken = Math.max(diff.conflicts - done_before, 0);

  async function run(postCloseReason?: string) {
    if (!file) return;
    setRefused(null);
    try {
      setDone(
        await apply.mutateAsync({
          file,
          columnMap,
          postCloseReason,
          meterChoices,
          //: **Only when it is actually a subset.** Sending `select_rows` with every line would be
          //: a different request for the same act, and the server records a selection — so a
          //: whole-file apply must look like one in the audit trail rather than like a selection
          //: that happened to include everything.
          selection: partial ? chosenLines : null,
          //: Every reason typed against a row this request writes. Surplus is not pruned here: a
          //: retry of a timed-out apply re-sends the same reasons for rows that are now
          //: `already_applied`, and `applied.py` accepts the surplus deliberately — pruning it
          //: client-side would re-break ADR-0039 for the one file it exists for.
          acknowledgements: Object.entries(reasons)
            .map(([line, why]) => ({ line: Number(line), why: why.trim() }))
            .filter((pair) => pair.why.length > 0),
        }),
      );
      setArming(false);
      //: Said to the stepper, which is the screen's, so step 4 reads *Done* only when the server
      //: answered. A refusal throws above and never reaches this line.
      onApplied();
    } catch (error) {
      setRefused(error instanceof Error ? error : new Error("That file could not be applied."));
    }
  }

  // What was applied, once it has been. The arming control is gone: a second apply of the same file
  // is safe but it is not a thing to invite, and the result is what the reader came for.
  if (done) {
    return (
      <div className={cls(own.applied)} data-testid="apply-result">
        <div className={cls(own.counts)}>
          <Count n={done.created} label="Added" tone="ok" />
          <Count n={done.updated} label="Corrected" tone="ok" />
          <Count n={done.already_applied} label="Already done" />
          {/* **Its own number, never folded into the others** (EXP-10). A row somebody chose to
              leave is neither a no-op nor a failure, and reporting "2 corrected" about a file with
              fifty changed rows while saying nothing about the other forty-eight is what makes a
              partial apply look like a complete one. */}
          <Count n={done.not_selected} label="Left out" />
          <Count n={done.refused} label="Refused" tone="warn" />
        </div>
        <p className={styles.note}>{done.sentence}</p>
        {/* **A count, and the reasons are deliberately not here** (rule 4). They are in
            `bulk_apply_acknowledgement`, which an erasure request can reach; the ledger carries
            the identifiers and the field name and never the words. */}
        {done.acknowledged > 0 ? (
          <p className={styles.note} data-testid="apply-acknowledged">
            {done.acknowledged === 1
              ? "One high-risk row was answered for."
              : `${done.acknowledged} high-risk rows were answered for.`}{" "}
            The reasons are recorded against the rows, not in the ledger entry — the chain carries
            who and what, never the words.
          </p>
        ) : null}
        {/* **Said in its own sentence** (ADR-0103): "3 added" does not tell a reader whether the
            stream those rows feed is now satisfied, and the answer depends on a choice they made. */}
        {(done.with_meter ?? 0) > 0 ? (
          <p className={styles.note} data-testid="apply-with-meter">
            {done.with_meter === 1
              ? "One added row was recorded against the meter you chose."
              : `${done.with_meter} added rows were recorded against the meters you chose.`}
          </p>
        ) : null}
        <p className={styles.note}>
          Nothing was overwritten. A corrected reading <strong>supersedes</strong> the one before it,
          and the figure it replaces keeps its receipt — open any of them from the register.
        </p>
        {/* Only the rows that did not go through. A list of 200 successes is a list nobody reads,
            and it would bury the four that need a person. */}
        {done.rows.filter((row) => row.outcome === "refused" || row.outcome === "already_applied")
          .length > 0 ? (
          <ul className={cls(own.rows)}>
            {done.rows
              .filter((row) => row.outcome === "refused" || row.outcome === "already_applied")
              .map((row) => (
                <li className={cls(own.row)} key={`${row.line}-${row.outcome}`}>
                  <span className={cls(own.rowHead)}>
                    <span
                      className={cls(own.tag, row.outcome === "refused" ? own.tagWarn : own.tagQuiet)}
                    >
                      {row.outcome === "refused" ? "Refused" : "Already done"}
                    </span>
                    <span className={cls(own.rowWhere)}>Row {row.line}</span>
                  </span>
                  <span className={cls(own.rowWhy)}>{row.because}</span>
                </li>
              ))}
          </ul>
        ) : null}
      </div>
    );
  }

  // **The retry, answered before it is attempted.** Every row of this file has already been
  // applied, so there is nothing left to do and — crucially — nothing went wrong. This is the
  // state a request that timed out at a proxy leaves behind, and it is the whole reason ADR-0039
  // exists; the screen that would not say so was the half that made the guarantee unusable.
  //: **The server's answer, not a count of zero** (EXP-10). `changes === 0` had five different
  //: meanings and rendered two of them; `nothing_to_apply` is a code, and the two it used to get
  //: wrong are the ones that matter — a file whose every row was refused, and a file naming rows
  //: nobody selected, both of which read as a quiet "nothing to do".
  //:
  //: The one case the server cannot answer is the local selection: the dry run does not know what
  //: has been un-ticked, so an empty selection is decided here and refused **before** a request
  //: that would predictably 422 (UX-04, design rule 6).
  //: Rows an Admin applies are not "selected away": when they are all that is left, the screen says
  //: so (below) rather than telling this person to tick rows they have no control for.
  const allHeld = held.length > 0 && chosenLines.length === 0 && canApply <= 0;
  const emptied = selectable.length > 0 && chosenLines.length === 0 && !allHeld;
  const nothing = emptied ? "nothing_selected" : allHeld ? "" : diff.nothing_to_apply;

  if (nothing === "all_already_applied" || (changes === 0 && done_before > 0)) {
    return (
      <p className={cls(own.pending)} data-testid="apply-already">
        <strong>
          {done_before === 1
            ? "This change has already been applied."
            : `These ${done_before} changes have already been applied.`}
        </strong>{" "}
        {filename || "This file"} was applied by an earlier attempt — if a request timed out, it
        finished. Nothing is outstanding and nothing will be written twice. The{" "}
        {done_before === 1 ? "reading is" : "readings are"} counted above as{" "}
        <strong>Already done</strong>: the version named in the file was superseded{" "}
        <em>by that apply</em>, not by somebody else.
      </p>
    );
  }

  //: **`changes === 0` is kept alongside the code, not replaced by it.** The code is better and
  //: narrower, and a screen that trusted it alone would fall through to "Apply 0 changes" against
  //: any response that did not carry one. Replacing the old guard with the new one is how a repair
  //: becomes a regression; the two are OR-ed, and the code only decides the *wording*.
  if (nothing || changes === 0) {
    return (
      <p className={cls(own.pending)} data-testid="apply-nothing">
        <strong>There is nothing to apply.</strong>{" "}
        {NOTHING[nothing] ||
          `No row of ${filename || "this file"} differs from the reading in force, so applying it would write nothing — which is not the same as it having failed.`}
      </p>
    );
  }

  // A Contributor prepared this and an Approver accepts it. Told, rather than shown a button that
  // predictably 403s (UX-04, and the same repair as F12's accept panel).
  if (mayApply === false) {
    return (
      <p className={cls(own.unauthorized)} data-testid="apply-not-permitted">
        <strong>An Admin or an Approver applies this.</strong> {roleName(role)} can run this
        comparison — the {changes === 1 ? "change" : "changes"} above {changes === 1 ? "is" : "are"}{" "}
        real and nothing is wrong with your file — but writing {changes === 1 ? "it" : "them"} into
        the inventory is an approval, because a corrected reading can move a figure in a report that
        has already been filed. Send them this file, or raise it as a task from the record.
      </p>
    );
  }

  //: **Every change in this file is one only an Admin can apply** (G-Wapprover-15). Said on the
  //: diff, before a reason is asked for: nothing here can be satisfied by this person.
  if (allHeld) {
    return (
      <p className={cls(own.unauthorized)} data-testid="apply-admin-only">
        <strong>
          {held.length === 1
            ? "The one change in this file needs an Admin."
            : `All ${held.length} changes in this file need an Admin.`}
        </strong>{" "}
        {held.length === 1 ? "It writes" : "They write"} into a period that has been closed, and
        only an Admin can record a change there. No reason is asked of you. Send this file to an
        Admin.
      </p>
    );
  }

  return (
    <div className={cls(own.apply)}>
      {/* **The held question** (ADR-0103). One picker per group of new rows being written, using the
          control the document import and the manual form use - so the rules (renders nothing for an
          empty list, never pre-selected, options are the registry's labels) hold in one place. The
          empty option is "Choose a meter", not "Not saying": here leaving it blank is not an answer,
          and the apply is held until every group has one. "None of these" is always offered, so the
          hold can be satisfied without a guess. */}
      {asked.length > 0 ? (
        <div className={cls(own.pending)} data-testid="apply-meter-groups">
          <p>
            <strong>Which meter were the new rows for?</strong> Each group below is at a site that
            expects readings from particular meters. Choose the meter and those rows count towards
            its stream; choose &ldquo;none of these&rdquo; and they are recorded with no meter, which
            counts when it is the only stream for that carrier at the site. Every row in a group takes the one
            answer, so if a group covers two meters, say none and split the file.
          </p>
          {asked.map((group) => (
            <MeterSelect
              key={meterGroupKey(group)}
              meters={group.meters}
              value={meters[meterGroupKey(group)] ?? ""}
              onChange={(meterId) => onMeter(meterGroupKey(group), meterId)}
              name={`Which meter were the ${group.category} rows at ${group.facility} for?`}
              label={`${group.rows} new ${group.category.replace(/_/g, " ")} row${group.rows === 1 ? "" : "s"} at ${group.facility}: which meter?`}
              note=""
              unansweredLabel="Choose a meter"
              noneLabel="None of these meters"
            />
          ))}
          {unanswered.length > 0 ? (
            <p className={styles.error} role="alert" data-testid="apply-meter-held">
              <strong>
                {unanswered.length === 1
                  ? "One group still needs an answer before this can be applied."
                  : `${unanswered.length} groups still need an answer before this can be applied.`}
              </strong>{" "}
              Nothing has been written.
            </p>
          ) : null}
        </div>
      ) : null}

      {held.length > 0 ? (
        <p className={cls(own.pending)} data-testid="apply-admin-held">
          <strong>
            {held.length === 1
              ? "One row needs an Admin and is left out of this apply."
              : `${held.length} rows need an Admin and are left out of this apply.`}
          </strong>{" "}
          {held.length === 1 ? "It writes" : "They write"} into a closed period. The other{" "}
          {canApply === 1 ? "change is" : `${canApply} changes are`} yours to apply.
        </p>
      ) : null}

      {/* **Only the conflicts somebody else caused.** A row this file already applied is also a
          conflict — its revision was superseded by that apply — and counting it here told a
          reviewer a stranger had been editing their inventory. */}
      {overtaken > 0 ? (
        <p className={cls(own.pending)}>
          <strong>
            {overtaken === 1
              ? "One row will be refused, and the rest will still apply."
              : `${overtaken} rows will be refused, and the rest will still apply.`}
          </strong>{" "}
          Somebody corrected {overtaken === 1 ? "that reading" : "those readings"} after this file
          was exported. Each is refused on its own and named afterwards — re-export and look at{" "}
          {overtaken === 1 ? "it" : "them"} rather than sending this file back whole.
        </p>
      ) : null}

      {done_before > 0 ? (
        <p className={cls(own.pending)} data-testid="apply-partly-done">
          <strong>
            {done_before === 1
              ? "One row has already been applied."
              : `${done_before} rows have already been applied.`}
          </strong>{" "}
          By an earlier attempt at this same file, not by anybody else.{" "}
          {done_before === 1 ? "It" : "They"} will be reported as already done and{" "}
          <strong>will not be written again</strong>.
        </p>
      ) : null}

      {/* **What the server will ask for, counted from the same sets the button is held on.** It
          adds no gate and removes none: `owed` and `unanswered` are what disable the button below,
          and this only says how many of each are left, so the person is not told by a held button.
          The reasons themselves are typed on each row in step 3. */}
      {asked.length > 0 || requiring.length > 0 ? (
        <ul className={cls(own.checklist)} data-testid="apply-checklist">
          {asked.length > 0 ? (
            <li>
              <StatusPill tone={unanswered.length === 0 ? "ok" : "warn"}>
                {asked.length - unanswered.length} of {asked.length} meter{" "}
                {asked.length === 1 ? "question" : "questions"} answered
              </StatusPill>
            </li>
          ) : null}
          {requiring.length > 0 ? (
            <li>
              <StatusPill tone={owed.length === 0 ? "ok" : "warn"}>
                {requiring.length - owed.length} of {requiring.length} high-risk{" "}
                {requiring.length === 1 ? "row has" : "rows have"} a reason
              </StatusPill>
            </li>
          ) : null}
        </ul>
      ) : null}

      {arming ? (
        <div className={cls(own.confirm)} data-testid="apply-confirm">
          <p>
            <strong>
              Apply {canApply} {canApply === 1 ? "change" : "changes"} to the inventory?
            </strong>{" "}
            {diff.updates > 0
              ? `${diff.updates === 1 ? "One reading is" : `${diff.updates} readings are`} corrected: the figure each replaces is superseded and keeps its receipt. `
              : ""}
            Sending the same file again afterwards changes nothing a second time, so a request that
            times out is safe to retry.
          </p>

          {/* **The refusal, said here rather than collected from the server** (EXP-10).
              `applied.py` refuses the whole request when a high-risk row has no reason, and until
              2026-10-11 this screen had no way to send one — so a customer who changed a unit in
              the spreadsheet was warned the change was significant, allowed to confirm it, and
              then refused with nothing to do about it. The browser gate was the first caller to
              reach that, on a write into a closed period.

              Naming the rows, because "some rows need a reason" on a 200-row file is a hunt. */}
          {owed.length > 0 ? (
            <p className={styles.error} role="alert" data-testid="apply-blocked">
              <strong>
                {owed.length === 1
                  ? `Row ${owed[0]} needs a reason before it can be applied.`
                  : `${owed.length} rows need a reason before they can be applied: ${owed.join(", ")}.`}
              </strong>{" "}
              Each one is high-risk, and the row says why. Say what you are doing and why, in the box
              beside it. Nothing has been written.
            </p>
          ) : null}

          <p className={cls(own.actions)}>
            <button
              type="button"
              className={styles.primary}
              //: **Disabled rather than refused by the server.** The server refuses either way;
              //: what this avoids is a button that predictably 422s, which is the same repair as
              //: the Contributor's accept panel (UX-04, F12).
              disabled={apply.isPending || owed.length > 0 || unanswered.length > 0}
              onClick={() => void run()}
              data-testid="apply-go"
            >
              {apply.isPending
                ? "Applying…"
                : choseFewer
                  ? `Yes, apply ${chosenLines.length} of ${selectable.length - held.length}`
                  : `Yes, apply ${canApply}`}
            </button>
            <button
              type="button"
              className={styles.fixLink}
              disabled={apply.isPending}
              onClick={() => setArming(false)}
            >
              Not now
            </button>
          </p>
        </div>
      ) : (
        <p className={cls(own.actions)}>
          <button
            type="button"
            className={styles.primary}
            // `undefined` while `/auth/me` is in flight: an enabled button before anyone knows
            // whether this role may use it is a button that 403s.
            disabled={mayApply === undefined || !file || unanswered.length > 0}
            onClick={() => setArming(true)}
            data-testid="apply"
          >
            {choseFewer
              ? `Apply ${chosenLines.length} of ${selectable.length - held.length} changes…`
              : `Apply ${canApply} ${canApply === 1 ? "change" : "changes"}…`}
          </button>
        </p>
      )}

      {/* A file that reaches a closed period (ADR-0094). Nothing was written, and the retry sends
          this same file, selection and reasons again with an Admin's reason on top. */}
      <PostCloseRetry
        error={refused}
        pending={apply.isPending}
        onRecord={(reason) => void run(reason)}
      />
      {refused && !isPeriodClosed(refused) ? (
        <p className={styles.error} role="alert" data-testid="apply-refused">
          {isAmbiguous(refused) ? (
            /* **The one branch that must not say "nothing was applied".** A 504 from a proxy or a
               dropped connection means the apply may well have run — EXP-35's canonical case — and
               claiming nothing was written is a false statement about the inventory.
               ADR-0039 is what makes the remedy simple: the idempotency key is derived per row
               server-side, so re-sending *this* file applies what is outstanding and reports the
               rest as already done. That is a guarantee this product has, and this is the place it
               pays for itself. */
            <>
              <strong>It is not known whether that was applied.</strong> The request did not
              complete, which is not the same as it not having happened. Send{" "}
              {filename || "the same file"} again: each row carries a key derived from the row
              itself, so anything that already went through is reported as already done rather
              than applied twice.
            </>
          ) : refused instanceof DiffRefused && refused.isUnauthorized ? (
            <>
              <strong>Refused: your role cannot apply changes.</strong> Nothing was written. An
              Admin or an Approver can apply this file.
            </>
          ) : refused instanceof DiffRefused && refused.isOutsidePeriod ? (
            <>
              <strong>Refused: this period is outside what you are engaged for.</strong> Nothing was
              written.
            </>
          ) : (
            <>
              <strong>Nothing was applied.</strong> {refused.message}
            </>
          )}
        </p>
      ) : null}
    </div>
  );
}

function Count({ n, label, tone }: { n: number; label: string; tone?: "ok" | "warn" | "crit" }) {
  return (
    <span className={cls(own.count, n > 0 && tone === "warn" && own.countWarn, n > 0 && tone === "crit" && own.countCrit)}>
      <span className={cls(own.countN, "num")}>{n}</span>
      <span className={cls(own.countLabel)}>{label}</span>
    </span>
  );
}

/** One row of the comparison, with the two decisions it may need (EXP-10).
 *
 * **Both controls live beside the row they are about**, which is the whole of the clause: a single
 * reason box under a 200-row apply collects one sentence about 200 different corrections, and a
 * single tickbox cannot say *which* rows. `evidence/corrections.py` already requires a reason per
 * field for one document; these two had disagreed, and T-23 settled it this way.
 *
 * **A row that will not be written carries neither.** `writes` is false for an unchanged row, a
 * conflict and an unreadable one, and the server excludes all three from both the selection and the
 * acknowledgement gate — so offering a tickbox for a row nothing can happen to would be a control
 * whose state is ignored, and asking for a reason would teach somebody to type "ok".
 */
function Row({
  row,
  editable,
  isAdmin,
  selected,
  onSelect,
  reason,
  onReason,
  onReceipt,
}: {
  row: DiffRow;
  editable: boolean;
  isAdmin: boolean;
  selected: boolean;
  onSelect: (wanted: boolean) => void;
  reason: string;
  onReason: (words: string) => void;
  onReceipt: (calculationId: string) => void;
}) {
  //: **Derived the same way the server derives it**, from `roundtrip.DiffRow.writes`: a create or
  //: an update writes, and nothing else does. There is no `writes` field on the wire, and adding
  //: one would be a second copy of a fact the disposition already carries — which is the reason
  //: `is_current` is not a column either.
  const writes = row.disposition === "create" || row.disposition === "update";
  //: **A row only an Admin can apply, seen by somebody who is not one** (G-Wapprover-15). No
  //: tickbox and no reason box: the server would refuse the whole file after the confirm, and a
  //: reason typed for a row the reader cannot apply is collected for nobody.
  const adminHeld = writes && row.needs_admin === true && !isAdmin;
  const meaning =
    (row.already_applied
      ? ALREADY_DONE
      : row.outside_window
        ? DISPOSITION.outside
        : DISPOSITION[row.disposition]) ?? { label: row.disposition, blurb: "" };
  return (
    <li className={cls(own.row)}>
      <span className={cls(own.rowHead)}>
        <span
          className={cls(
            own.tag,
            row.disposition === "conflict" && !row.already_applied && own.tagWarn,
            row.already_applied && own.tagQuiet,
            row.disposition === "invalid" && !row.outside_window && own.tagCrit,
            row.outside_window && own.tagWarn,
            row.disposition === "unchanged" && own.tagQuiet,
          )}
        >
          {meaning.label}
        </span>
        <span className={cls(own.rowWhere)}>
          Row {row.line}
          {row.facility ? ` · ${row.facility}` : ""}
        </span>
      </span>
      <span className={cls(own.rowWhy)}>{row.because}</span>
      {/* **This new row asks a meter question** (ADR-0103), said on the row itself because a
          reviewer reading one row should not have to cross-check a note at the top. */}
      {row.needs_meter_choice ? (
        <p className={cls(own.rowWhy)} data-testid={`needs-meter-${row.line}`}>
          <strong>Needs a meter chosen:</strong> this reading is at a site that expects readings from
          particular meters. Whoever applies the file says which one it came off.
        </p>
      ) : null}
      {/* Both values, always. A diff showing only the new one is a list of assertions rather than a
          comparison, and there is nothing for a reviewer to check it against (EXP-10). */}
      {row.changes.length > 0 ? (
        <ul className={cls(own.changes)}>
          {row.changes.map((change) => (
            <li key={change.field}>
              <span className={cls(own.field)}>{change.field.replace(/_/g, " ")}</span>
              <span className={cls(own.was, "num")}>{change.was || "—"}</span>
              <span className={cls(own.arrow)} aria-hidden="true">
                →
              </span>
              <span className={cls(own.now, "num")}>{change.now || "—"}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {/* **The figure this change is about, not a figure it proposes** (EXP-10's *impact*). A
          proposed figure has no calculation, no locked factor and no ledger entry, so it cannot
          produce a receipt and rule 3 says it is not shown. What a reviewer is deciding about is
          the emission in force today, and that one resolves. */}
      {(row.figures_in_force ?? []).length > 0 ? (
        <ul className={cls(own.changes)} data-testid={`apply-impact-${row.line}`}>
          {(row.figures_in_force ?? []).map((figure) => (
            <li key={figure.calculation_id}>
              <span className={cls(own.field)}>in force now</span>
              {/* **Rounded here and nowhere else** (rule 5). `kgco2e` is a `NUMERIC` string, and a
                  stored scale has reached a screen in this product before — `4200.0000000000`. */}
              <span className={cls(own.was, "num")}>{tonnes(figure.kgco2e, 2)} tCO₂e</span>
              <span className={cls(own.arrow)} aria-hidden="true">
                ·
              </span>
              {/* **Opened through the screen, so the drawer is rule 3's single rendering of the
                  chain.** `ScreenFrame` holds the drawer and the id lives in the URL, so a receipt
                  opened from here is the same receipt the register opens — not a second view of
                  the same figure that could come to disagree with the first. */}
              <button
                type="button"
                className={cls(own.now, styles.fixLink)}
                onClick={() => onReceipt(figure.calculation_id)}
              >
                receipt
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {/* **The hazards, named.** `because` is one sentence over whatever this row carries; these
          are the facts the reason below is attached to. Shown whether or not the reader may apply,
          because a Contributor preparing the file is the person who can still fix it. */}
      {(row.risks ?? []).length > 0 ? (
        <p className={cls(own.rowWhy)} data-testid={`apply-risks-${row.line}`}>
          <strong>Needs answering for:</strong>{" "}
          {(row.risks ?? []).map((code) => RISK[code] ?? code).join("; ")}.
        </p>
      ) : null}

      {adminHeld ? (
        <p className={cls(own.rowWhy)} data-testid={`admin-held-${row.line}`}>
          <strong>Left out of your apply:</strong> an Admin applies this row.
        </p>
      ) : null}

      {editable && writes && !adminHeld ? (
        <p className={cls(own.rowWhy)}>
          <label>
            <input
              type="checkbox"
              checked={selected}
              onChange={(event) => onSelect(event.target.checked)}
              data-testid={`row-select-${row.line}`}
            />{" "}
            Apply this row
          </label>
        </p>
      ) : null}

      {/* **Only while it is still being applied.** A row somebody has un-ticked is one they said no
          to, and the server stops asking for its reason — so a box that stayed would be collecting
          an answer that is no longer wanted and, worse, would keep the apply disabled. */}
      {editable && writes && !adminHeld && row.requires_acknowledgement && selected ? (
        <p className={cls(own.rowWhy)}>
          <label>
            <span>
              <strong>Why are you making this change?</strong> It is recorded against this row and
              is not part of the ledger entry.
            </span>{" "}
            <input
              type="text"
              value={reason}
              onChange={(event) => onReason(event.target.value)}
              placeholder="e.g. corrected against the supplier's re-issued invoice"
              data-testid={`ack-reason-${row.line}`}
            />
          </label>
        </p>
      ) : null}
    </li>
  );
}

export function BulkScreen() {
  return (
    <RequireSession>
      <RequirePeriod title="Bulk edit" crumb="export, edit, and see what would change">
        {(screen, period) => <BulkScreenBody screen={screen} period={period} />}
      </RequirePeriod>
    </RequireSession>
  );
}
